import { Edit } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Card,
  CardContent,
  Checkbox,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { createFilesToSign } from 'controllers/conformity/index'
import { ConformityListDto, ReadyToSignDto } from 'dtos/conformity'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clientSideOrderBy, clientSidePagination, formatDateMoment, toYesNoOrUndefined } from 'utils'

interface ReadyToSignCardProps {
  // readyToSign is each row in the table
  readyToSign: ReadyToSignDto[]
  refreshLists: (conformityListDto: ConformityListDto) => void
}

export default function ReadyToSignCard({ readyToSign, refreshLists }: ReadyToSignCardProps) {
  const navigate = useNavigate()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof ReadyToSignDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<ReadyToSignDto[]>([])

  const onChangeSortByDirection = (propertyName: keyof ReadyToSignDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Ready To Process
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'></TableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='importerStockNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Importer Stock #
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='exporterCompany' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Exporter
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='inboundTripNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Inbound Trip #
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='papsNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      PAPS #
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='entryNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Entry #
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='entryDate' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Entry Date
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      VIN
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Year
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Make
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Model
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='convertCluster' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Convert Cluster
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto>
                      keyName='clusterReinstallationCompletedDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Cluster Reinstallation Complete Date
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='hasOpenRecalls' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Open Recalls
                    </OrderableTableCell>

                    <OrderableTableCell<ReadyToSignDto> keyName='dotRejectionDate' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      DOT Rejection Date
                    </OrderableTableCell>

                    <TableCell size='small'></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {readyToSign
                    ?.sort(clientSideOrderBy<ReadyToSignDto>(orderBy, order))
                    .slice(...clientSidePagination(page, pageSize))
                    .map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox'>
                          {vehicle.canCreateFilesToSign && (
                            <Checkbox
                              checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                              disabled={isSubmitting}
                              onChange={() => {
                                if (selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)) {
                                  setSelected(selected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid))
                                } else {
                                  setSelected([...selected, vehicle])
                                }
                              }}
                            />
                          )}
                        </TableCell>

                        <TableCell>{vehicle.importerStockNumber}</TableCell>

                        <TableCell>{vehicle.exporterCompany}</TableCell>

                        <TableCell>
                          {vehicle.inboundTripGuid && vehicle.inboundTripNumber ? (
                            <Link onClick={() => navigate(`/inbound-trips/${encodeURIComponent(vehicle.inboundTripGuid!)}`)} sx={{ cursor: 'pointer' }}>
                              {vehicle.inboundTripNumber}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>

                        <TableCell>{vehicle.papsNumber ?? '-'}</TableCell>

                        <TableCell
                          sx={theme => ({
                            color: Boolean(vehicle.entryNumber) ? 'inherit' : theme.palette.error.main
                          })}
                        >
                          {vehicle.entryNumber ?? 'Missing'}
                        </TableCell>

                        <TableCell
                          sx={theme => ({
                            color: Boolean(vehicle.entryDate) ? 'inherit' : theme.palette.error.main
                          })}
                        >
                          {vehicle.entryDate ? formatDateMoment(vehicle.entryDate) : 'Missing'}
                        </TableCell>

                        <TableCell>{vehicle.vin}</TableCell>

                        <TableCell>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        <TableCell>{toYesNoOrUndefined(vehicle.convertCluster) ?? '-'}</TableCell>

                        <TableCell>
                          {vehicle.clusterReinstallationCompletedDateTime ? formatDateMoment(vehicle.clusterReinstallationCompletedDateTime) : '-'}
                        </TableCell>

                        <TableCell>
                          {vehicle.hasOpenRecalls ? (
                            <Link onClick={() => navigate(`/vehicles/${encodeURIComponent(vehicle.vehicleGuid)}/recall`)} sx={{ cursor: 'pointer' }}>
                              Yes
                            </Link>
                          ) : (
                            (toYesNoOrUndefined(vehicle.hasOpenRecalls) ?? '-')
                          )}
                        </TableCell>

                        <TableCell>{vehicle.dotRejectionDate ? formatDateMoment(vehicle.dotRejectionDate) : '-'}</TableCell>

                        <TableCell size='small'>
                          <LoadingButton
                            endIcon={<Edit fontSize='small' />}
                            loading={isSubmitting}
                            onClick={() => {
                              navigate(`/vehicles/${encodeURIComponent(vehicle.vehicleGuid)}/conformity`)
                            }}
                            size='small'
                            variant='text'
                          >
                            EDIT
                          </LoadingButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={readyToSign.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Ready To Process Table:**

Vehicles appear in the Ready To Process table when vehicle check-in is complete. Damage and Title status do not impact the vehicle’s conformity eligibility.

**Table Row Check Box:**

For the Select checkbox to appear in a vehicle’s row within the Ready To Process table, the following conditions must be met:
   - Entry # and Entry Date columns must be populated for that vehicle.
   - If Convert Cluster = Yes, then Cluster Reinstallation Completed must be populated.
   - Open Recalls must be No.

**Inbound Trip Column:**

The Inbound Trip # the vehicle is associated with is a clickable link that opens the Inbound Trip Details screen.

**Entry # Column:**

Displays the Entry # from Inbound Trips.

If no Entry # exists, the word “Missing” will be displayed in red text.

**Entry Date Column:**

Displays the Entry Date from Inbound Trips.

If no Entry Date exists, the word “Missing” will be displayed in red text.

**Convert Cluster Column:**

Displays Yes if the vehicle’s cluster has been converted.

Displays No if the vehicle’s cluster has not been converted.

**Cluster Reinstallation Complete Date:**

Displays the date when the Cluster Reinstallation was completed.

If the Cluster Reinstallation has not been completed, a “-” will be displayed.

**Open Recalls Column:**

Displays Yes or No based on whether the vehicle has any open recalls.

If there is an open recall, “Yes” will be displayed as a clickable link. Clicking the link navigates the user to the Vehicle Details – Recall tab.

**DOT Rejection Column:**

The DOT Rejection column supports vehicles looping through the Conformity process.

At the end of the Conformity process in VTS2, a user uploads the Conformity ZIP file for the vehicle to the DOT portal (external website).

If the DOT rejects the vehicle’s Conformity packet, a user must:
   - Navigate to the Vehicle Details – Conformity tab.
   - Enter the rejection details.
   - Upload the rejection email.

If the vehicle does not have a DOT Rejection, a “-” will be displayed.

**Edit Button:**

Clicking the Edit button navigates the user to the Vehicle Details – Conformity tab.

**Create Files Button:**

Clicking the Create Files button generates the Signed Statement of Conformity and the Warranty Certificate for each selected vehicle. The Signed Statement of Conformity and Warranty Certificate then appears under the Vehicle Details – Documents (tab) for each respective vehicle.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={selected.length === 0}
                loading={isSubmitting}
                onClick={() => {
                  setIsSubmitting(true)
                  createFilesToSign(selected)
                    .then(res => {
                      enqueueSnackbar('Vehicles Saved Successfully!', { variant: 'success' })
                      refreshLists(res.value)
                      setSelected([])
                    })
                    .finally(() => {
                      setIsSubmitting(false)
                    })
                }}
                variant='contained'
              >
                CREATE FILES
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
